import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { CustomButton } from '../../../../../components/button/Button';
import { ModalBox } from '../../../../../components/modal/ModalBox';
import { GroupStandingsTieBreakerEnum } from '../../../../../types/enums';
import {
  GROUP_STANDINGS_TIE_BREAKER_DESCRIPTION_KEYS,
  GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS,
  GroupStandingsTieBreakerResolution,
  hasRequiredGroupStandingsTieBreakers,
  insertMissingV2TeamCriteria,
  requiredGroupStandingsTieBreakersErrorKey,
  TeamMatchupScoring,
} from '../../../../../utils/groupStandingsTieBreakers';
import {
  GroupStandingsTieBreakerSection,
  TieBreakerChainContextScope,
} from './GroupStandingsTieBreakerSection';
import './GroupStandingsTieBreakerModalControl.scss';

type GroupStandingsTieBreakerModalControlProps = {
  value: GroupStandingsTieBreakerEnum[];
  onChange: (nextValue: GroupStandingsTieBreakerEnum[]) => void;
  inheritChecked?: boolean;
  onInheritChange?: (checked: boolean) => void;
  inheritLabelKey?: string;
  editorDisabled?: boolean;
  inheritDisabled?: boolean;
  triggerDisabled?: boolean;
  className?: string;
  resolution?: GroupStandingsTieBreakerResolution;
  inheritScope?: 'class' | 'tournament' | 'tour';
  chainContextScope?: TieBreakerChainContextScope;
  isTeamsClass?: boolean;
  /** Increment to open the editor (e.g. after a failed class save). */
  openSignal?: number;
  scoringValue?: TeamMatchupScoring;
  onScoringChange?: (nextValue: TeamMatchupScoring) => void;
  inheritScoringChecked?: boolean;
  onInheritScoringChange?: (checked: boolean) => void;
  inheritScoringLabelKey?: string;
  showOptInRankingWarning?: boolean;
};

export const GroupStandingsTieBreakerModalControl = ({
  value,
  onChange,
  inheritChecked,
  onInheritChange,
  inheritLabelKey,
  editorDisabled = false,
  inheritDisabled = false,
  triggerDisabled = false,
  className,
  resolution,
  inheritScope = 'class',
  chainContextScope,
  isTeamsClass,
  openSignal,
  scoringValue,
  onScoringChange,
  inheritScoringChecked,
  onInheritScoringChange,
  inheritScoringLabelKey,
  showOptInRankingWarning = false,
}: GroupStandingsTieBreakerModalControlProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<GroupStandingsTieBreakerEnum[]>(value);
  const [draftInheritChecked, setDraftInheritChecked] = useState<boolean | undefined>(
    inheritChecked
  );
  const [draftScoringValue, setDraftScoringValue] = useState<TeamMatchupScoring | undefined>(
    scoringValue
  );
  const [draftInheritScoringChecked, setDraftInheritScoringChecked] = useState<boolean | undefined>(
    inheritScoringChecked
  );

  const hasInheritanceToggle =
    typeof inheritChecked === 'boolean' && Boolean(onInheritChange) && Boolean(inheritLabelKey);

  const summaryItems = useMemo(() => {
    if (hasInheritanceToggle && inheritChecked) {
      return [];
    }

    return (value || []).slice(0, 3);
  }, [hasInheritanceToggle, inheritChecked, value]);

  const inheritedSummary =
    hasInheritanceToggle && inheritChecked ? t(inheritLabelKey as string) : null;

  const resolveInheritedOrder = (): GroupStandingsTieBreakerEnum[] | null => {
    if (!resolution) {
      return null;
    }

    let raw: GroupStandingsTieBreakerEnum[] | null = null;

    if (inheritScope === 'class') {
      raw =
        resolution.chain.tournament ??
        resolution.chain.tour ??
        resolution.chain.organization ??
        resolution.chain.default;
    } else if (inheritScope === 'tournament') {
      raw = resolution.chain.tour ?? resolution.chain.organization ?? resolution.chain.default;
    } else {
      raw = resolution.chain.organization ?? resolution.chain.default;
    }

    if (!raw?.length) {
      return null;
    }

    return [...raw];
  };

  const openModal = () => {
    if (hasInheritanceToggle && inheritChecked) {
      const inheritedOrder = resolveInheritedOrder();
      setDraftValue(inheritedOrder?.length ? inheritedOrder : value);
    } else {
      setDraftValue(value);
    }
    setDraftInheritChecked(inheritChecked);
    setDraftScoringValue(scoringValue);
    setDraftInheritScoringChecked(inheritScoringChecked);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (!openSignal) {
      return;
    }
    openModal();
    // openModal is intentionally omitted: we only react to a new signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  const isDraftInheriting = hasInheritanceToggle && Boolean(draftInheritChecked);

  const draftHasRequiredCore =
    isDraftInheriting || hasRequiredGroupStandingsTieBreakers(draftValue, { isTeamsClass });

  const handleSave = () => {
    onChange(draftValue);
    if (hasInheritanceToggle && typeof draftInheritChecked === 'boolean') {
      onInheritChange?.(draftInheritChecked);
    }
    if (onScoringChange && draftScoringValue) {
      onScoringChange(draftScoringValue);
    }
    if (
      typeof inheritScoringChecked === 'boolean' &&
      typeof draftInheritScoringChecked === 'boolean'
    ) {
      onInheritScoringChange?.(draftInheritScoringChecked);
    }
    closeModal();
  };

  const resolvedChainContext: TieBreakerChainContextScope =
    chainContextScope ??
    (inheritScope === 'tournament' ? 'tournament' : inheritScope === 'tour' ? 'tour' : 'class');

  return (
    <>
      <div className={`group-standings-tie-breaker-modal-control ${className || ''}`.trim()}>
        <CustomButton
          variant="outline-dark"
          btnText={t('groupStandingsTieBreaker-openEditor')}
          cb={openModal}
          disabled={triggerDisabled}
          className="group-standings-tie-breaker-modal-control__button"
        />
        <div className="group-standings-tie-breaker-modal-control__summary-block">
          {inheritedSummary ? (
            <span className="group-standings-tie-breaker-modal-control__summary">
              {inheritedSummary}
            </span>
          ) : summaryItems.length ? (
            <div className="group-standings-tie-breaker-modal-control__summary-items">
              {summaryItems.map((item) => {
                const labelKey = GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS[item];
                const descriptionKey = GROUP_STANDINGS_TIE_BREAKER_DESCRIPTION_KEYS[item];
                return (
                  <span
                    key={item}
                    className="group-standings-tie-breaker-modal-control__summary-item"
                  >
                    <span>{t(labelKey)}</span>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{t(descriptionKey)}</Tooltip>}
                    >
                      <InfoCircleFill
                        size={12}
                        className="group-standings-tie-breaker-modal-control__info-icon"
                      />
                    </OverlayTrigger>
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="group-standings-tie-breaker-modal-control__summary">
              {t('groupStandingsTieBreaker-noCriteriaConfigured')}
            </span>
          )}
        </div>
      </div>

      <ModalBox
        show={isOpen}
        onHide={closeModal}
        handleSave={handleSave}
        isDisabled={!draftHasRequiredCore}
        title={t('groupStandingsTieBreaker-title')}
        size="lg"
        withoutMinHeight
      >
        <div>
          {!draftHasRequiredCore && !isDraftInheriting ? (
            <p
              className="group-standings-tie-breaker-modal-control__core-error"
              data-testid="group-standings-tie-breaker-core-error"
            >
              {t(requiredGroupStandingsTieBreakersErrorKey(draftValue, { isTeamsClass }))}
            </p>
          ) : null}
          <GroupStandingsTieBreakerSection
            value={draftValue}
            onChange={(next) => {
              setDraftValue(next);
            }}
            description={t('groupStandingsTieBreaker-description')}
            inheritChecked={draftInheritChecked}
            onInheritChange={(checked) => {
              setDraftInheritChecked(checked);
              if (!checked) {
                if (isTeamsClass) {
                  setDraftValue(insertMissingV2TeamCriteria(draftValue));
                }
                return;
              }
              const inheritedOrder = resolveInheritedOrder();
              if (inheritedOrder?.length) {
                setDraftValue(inheritedOrder);
              }
            }}
            inheritLabelKey={inheritLabelKey}
            editorDisabled={
              editorDisabled || (hasInheritanceToggle && Boolean(draftInheritChecked))
            }
            inheritDisabled={inheritDisabled}
            showTitle={false}
            resolution={resolution}
            chainContextScope={resolvedChainContext}
            isTeamsClass={isTeamsClass}
            scoringValue={draftScoringValue}
            onScoringChange={onScoringChange ? setDraftScoringValue : undefined}
            inheritScoringChecked={draftInheritScoringChecked}
            onInheritScoringChange={
              onInheritScoringChange ? setDraftInheritScoringChecked : undefined
            }
            inheritScoringLabelKey={inheritScoringLabelKey}
            showOptInRankingWarning={showOptInRankingWarning && isTeamsClass}
          />
        </div>
      </ModalBox>
    </>
  );
};
